// crewbit-admin/src/components/money/RemittanceLogTableRow.tsx
//
// Single <tr> for the admin Remittance Log table. Renders one
// RemittanceLogRow with cells for Period / Tax Type / Status / Amount Owed /
// Filed Date.
//
// Click dispatches by status:
//   'open' OR 'reopened' →  MarkFiledPanel (file or re-file post-amendment)
//   'filed' OR 'amended' →  ReopenRemittancePanel (owner-only — caller gates)
//
// Tabular nums on every numeric per Hard Rule #7.

import type { RemittanceLogRow } from '../../hooks/useRemittanceLog';
import { formatMoney } from '../../lib/money';
import { palette } from '../../styles/tokens';

interface RemittanceLogTableRowProps {
  row: RemittanceLogRow;
  onClick: (row: RemittanceLogRow) => void;
}

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13,
  color: palette.g700,
  borderBottom: `1px solid ${palette.g100}`,
  verticalAlign: 'middle',
};

interface StatusToken {
  bg: string;
  fg: string;
  label: string;
}

function statusToken(status: RemittanceLogRow['status']): StatusToken {
  switch (status) {
    case 'open':
      return { bg: palette.g100, fg: palette.g700, label: 'Open' };
    case 'filed':
      return { bg: palette.greenWash, fg: palette.green, label: 'Filed' };
    case 'amended':
      return { bg: palette.amberWash, fg: palette.amber, label: 'Amended' };
    case 'reopened':
      return { bg: palette.redWash, fg: palette.red, label: 'Reopened' };
  }
}

function formatPeriod(periodStart: string, periodEnd: string): string {
  const s = new Date(periodStart);
  const e = new Date(periodEnd);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short' };
  return `${s.toLocaleDateString('en-CA', opts)}–${e.toLocaleDateString('en-CA', { ...opts, year: 'numeric' })}`;
}

function formatFiledDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function RemittanceLogTableRow({ row, onClick }: RemittanceLogTableRowProps) {
  const tone = statusToken(row.status);
  const period = formatPeriod(row.period_start, row.period_end);

  return (
    <tr
      onClick={() => onClick(row)}
      style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = palette.g50)}
      onMouseLeave={(e) => (e.currentTarget.style.background = '')}
    >
      <td style={{ ...tdStyle, fontWeight: 600, color: palette.g800 }}>{period}</td>
      <td style={{ ...tdStyle, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 12, color: palette.g600 }}>
        {row.tax_type}
      </td>
      <td style={tdStyle}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11.5,
            fontWeight: 600,
            background: tone.bg,
            color: tone.fg,
            whiteSpace: 'nowrap',
          }}
        >
          {tone.label}
        </span>
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: palette.g800,
        }}
      >
        {formatMoney(row.amount_owed)}
      </td>
      <td style={{ ...tdStyle, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
        {formatFiledDate(row.filed_at)}
      </td>
    </tr>
  );
}
